<template>
  <el-container class="layout">
    <!-- 侧边栏（FastAdmin 风格，可折叠） -->
    <el-aside :width="isCollapse ? '64px' : '230px'" class="aside">
      <div class="logo" :class="{ 'is-collapse': isCollapse }">
        <span v-show="!isCollapse" class="logo-badge">
          <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
          </svg>
        </span>
        <span v-show="!isCollapse" class="logo-text">{{ companyName }}</span>
        <button
          class="collapse-btn"
          :title="isCollapse ? '展开菜单' : '收起菜单'"
          @click="isCollapse = !isCollapse"
        >
          <i :class="isCollapse ? 'el-icon-s-unfold' : 'el-icon-s-fold'"></i>
        </button>
      </div>

      <el-scrollbar class="menu-scroll">
        <el-menu
          class="sidebar-menu"
          router
          :collapse="isCollapse"
          :collapse-transition="false"
          :default-active="activeMenu"
          background-color="#222D32"
          text-color="#B8C7CE"
          active-text-color="#FFFFFF"
        >
          <template v-for="group in menus">
            <!-- 一级菜单项 -->
            <el-menu-item v-if="group.type === 'item'" :key="group.key" :index="group.path">
              <i :class="group.icon"></i>
              <span slot="title">{{ group.title }}</span>
            </el-menu-item>
            <!-- 分组菜单 -->
            <el-submenu v-else :key="group.key" :index="group.key">
              <template slot="title">
                <i :class="group.icon"></i>
                <span>{{ group.title }}</span>
              </template>
              <el-menu-item v-for="child in group.children" :key="child.path" :index="child.path">
                {{ child.title }}
              </el-menu-item>
            </el-submenu>
          </template>
        </el-menu>
      </el-scrollbar>
    </el-aside>

    <!-- 主区 -->
    <el-container class="main-wrap">
      <el-header class="header" height="56px">
        <div class="header-left">
          <span class="page-title">{{ pageTitle }}</span>
        </div>
        <div class="header-right">
          <span class="user-name">{{ userName }}</span>
          <el-tag v-if="roleTag" size="mini" class="role-tag" effect="plain">{{ roleTag }}</el-tag>
          <el-button type="text" class="logout-btn" @click="pwdVisible = true">
            <i class="el-icon-key"></i> 修改密码
          </el-button>
          <el-button type="text" class="logout-btn" @click="handleLogout">
            <i class="el-icon-switch-button"></i> 退出
          </el-button>
        </div>
      </el-header>

      <el-main class="content">
        <router-view />
      </el-main>
    </el-container>

    <!-- 修改密码弹窗 -->
    <el-dialog title="修改密码" :visible.sync="pwdVisible" width="420px" :close-on-click-modal="false">
      <el-form ref="pwdForm" :model="pwdForm" :rules="pwdRules" label-width="90px" size="small">
        <el-form-item label="原密码" prop="oldpassword">
          <el-input v-model="pwdForm.oldpassword" type="password" show-password placeholder="请输入原密码" />
        </el-form-item>
        <el-form-item label="新密码" prop="newpassword">
          <el-input v-model="pwdForm.newpassword" type="password" show-password placeholder="至少 6 位" />
        </el-form-item>
        <el-form-item label="确认新密码" prop="confirm">
          <el-input v-model="pwdForm.confirm" type="password" show-password placeholder="再次输入新密码" />
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button size="small" @click="pwdVisible = false">取 消</el-button>
        <el-button type="primary" size="small" :loading="pwdLoading" @click="doChangePwd">确认修改</el-button>
      </div>
    </el-dialog>
  </el-container>
</template>

<script>
import { mapState } from 'vuex'
import { filterMenus } from '@/config/menus'
import { changePwd } from '@/api/auth'

const TYPE_LABEL = {
  company: '机构管理员',
  unit: '战队',
  team: '小组',
  staff: '员工'
}

export default {
  name: 'Layout',
  data() {
    return {
      isCollapse: false,
      // 修改密码
      pwdVisible: false,
      pwdLoading: false,
      pwdForm: { oldpassword: '', newpassword: '', confirm: '' },
      pwdRules: {
        oldpassword: [{ required: true, message: '请输入原密码', trigger: 'blur' }],
        newpassword: [
          { required: true, message: '请输入新密码', trigger: 'blur' },
          { min: 6, message: '新密码至少 6 位', trigger: 'blur' }
        ],
        confirm: [
          { required: true, message: '请再次输入新密码', trigger: 'blur' },
          { validator: (r, v, cb) => { if (v !== this.pwdForm.newpassword) cb(new Error('两次输入的新密码不一致')); else cb() }, trigger: 'blur' }
        ]
      }
    }
  },
  computed: {
    ...mapState(['user', 'smsEnabled']),
    // 按身份过滤后的菜单
    menus() {
      return filterMenus(this.user, { smsEnabled: this.smsEnabled })
    },
    activeMenu() {
      return this.$route.path
    },
    pageTitle() {
      return (this.$route.meta && this.$route.meta.title) || ''
    },
    userName() {
      return (this.user && this.user.name) || ''
    },
    companyName() {
      return (this.user && this.user.company && this.user.company.name) || ''
    },
    roleTag() {
      if (!this.user) return ''
      if (this.user.type === 'company') return TYPE_LABEL.company
      if (this.user.is_promo) return '推广员工'
      if (this.user.is_leader) return '队长'
      return TYPE_LABEL[this.user.type] || ''
    }
  },
  created() {
    // 仅短信菜单可见身份（机构管理员/队长）拉取模块启用状态，关闭时隐藏短信群发菜单
    const u = this.user
    const canStaffLeader = (u && (u.type === 'company' || (u.type === 'staff' && parseInt(u.is_leader) === 1)))
    if (canStaffLeader) this.$store.dispatch('loadSmsFeature')
  },
  methods: {
    async handleLogout() {
      await this.$store.dispatch('logout')
      this.$router.replace('/login')
      this.$message.success('已退出登录')
    },
    doChangePwd() {
      this.$refs.pwdForm.validate(valid => {
        if (!valid) return
        this.pwdLoading = true
        changePwd(this.pwdForm.oldpassword, this.pwdForm.newpassword)
          .then(() => {
            this.$message.success('密码已修改，请重新登录')
            this.pwdVisible = false
            this.handleLogout() // 后端已踢当前会话，回到登录页
          })
          .catch(() => {})
          .finally(() => { this.pwdLoading = false })
      })
    }
  }
}
</script>

<style scoped>
.layout {
  height: 100vh;
}

/* ===== 侧边栏（参考 FastAdmin skin-black-blue） ===== */
.aside {
  background: #222D32;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transition: width 0.25s;
}

.logo {
  height: 50px;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 18px;
  color: #fff;
  background: #222D32;
  border-bottom: 1px solid #181F23;
  flex-shrink: 0;
  white-space: nowrap;
}

.logo.is-collapse {
  justify-content: center;
  padding: 0;
}

.logo:hover {
  background: #202A2F;
}

.logo-badge {
  width: 30px;
  height: 30px;
  border-radius: 7px;
  background: linear-gradient(135deg, #3668FC, #5B8DFF);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
}

.logo-text {
  font-size: 16px;
  font-weight: 700;
  letter-spacing: 1px;
  max-width: 150px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.menu-scroll {
  flex: 1;
  overflow: hidden;
}

.sidebar-menu {
  border-right: none;
}

/* 菜单项默认与 hover（FastAdmin：hover #1E282C） */
.sidebar-menu /deep/ .el-menu-item {
  height: 46px;
  line-height: 46px;
}

.sidebar-menu /deep/ .el-menu-item:hover,
.sidebar-menu /deep/ .el-submenu__title:hover {
  background-color: #1E282C !important;
  color: #FFFFFF !important;
}

/* 激活菜单项：FastAdmin 实色蓝 #4E73DF + 白字 */
.sidebar-menu /deep/ .el-menu-item.is-active {
  background-color: #4E73DF !important;
  color: #FFFFFF !important;
}

/* 子菜单展开时父级背景更深 #181F23 */
.sidebar-menu /deep/ .el-submenu.is-opened > .el-submenu__title {
  background-color: #181F23;
}

.sidebar-menu /deep/ .el-submenu__title {
  height: 46px;
  line-height: 46px;
}

/* 一级菜单项（工作台/协议/统计）：与 submenu 标题保持默认对齐 */
.sidebar-menu /deep/ .el-menu-item {
  color: #B8C7CE;
}

/* 子菜单项：文字 #6C8C9B、左缩进 48px，hover/激活变白 */
.sidebar-menu /deep/ .el-submenu .el-menu {
  background-color: #181F23 !important;
}

.sidebar-menu /deep/ .el-submenu .el-menu-item {
  color: #6C8C9B !important;
  background-color: #181F23 !important;
  padding-left: 48px !important;
}

.sidebar-menu /deep/ .el-submenu .el-menu-item.is-active {
  color: #FFFFFF !important;
  background-color: #4E73DF !important;
}

.sidebar-menu /deep/ .el-submenu .el-menu-item:hover {
  color: #FFFFFF !important;
  background-color: #1E282C !important;
}

/* ===== 主区：简约白色卡片 ===== */
.main-wrap {
  background: #F2F5FB;
}

.header {
  background: #FFFFFF;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0 1px 4px rgba(27, 42, 77, 0.08);
  z-index: 2;
}

.page-title {
  font-size: 16px;
  font-weight: 600;
  color: #18233C;
}

.header-left {
  display: flex;
  align-items: center;
}

/* 折叠按钮（位于侧边栏 logo 内，适配深色背景） */
.collapse-btn {
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 18px;
  color: #A8B6BF;
  flex-shrink: 0;
  transition: background 0.2s, color 0.2s;
}

/* 展开态：按钮靠 logo 最右 */
.logo .collapse-btn {
  margin-left: auto;
}

/* 折叠态：仅按钮，水平居中 */
.logo.is-collapse .collapse-btn {
  margin-left: 0;
}

.collapse-btn:hover {
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 10px;
}

.user-name {
  font-size: 13px;
  color: #18233C;
  font-weight: 600;
}

.role-tag {
  border-radius: 4px;
}

.logout-btn {
  font-size: 13px;
  color: #8A94AD;
}

.logout-btn:hover {
  color: #3668FC;
}

.content {
  padding: 20px;
  overflow-y: auto;
}

/* 白色卡片统一风格：页面根卡片圆角阴影 */
.content /deep/ .el-card {
  border: none;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(27, 42, 77, 0.06) !important;
}
</style>
